import { addGradient } from './style'
import dompurify from 'dompurify'

const addExternalAria = (el) => {
  const links = el.querySelectorAll('a')
  Array.from(links).forEach((link) => {
    if (link.target === '_blank') {
      link.setAttribute('aria-label', 'link opens in new tab or window')
    }
  })
  return el[el.classList.contains('has-table') ? 'outerHTML' : 'innerHTML'].toString()
}

const addTableClasses = (el) => {
  el.classList.add(el.querySelectorAll('table').length > 0 ? 'has-table' : 'no-table')
  el.querySelectorAll('td').forEach(td => {
    if (td.childNodes.length == 1 && td.childNodes[0].tagName == 'SPAN') {
      td.childNodes[0].classList.add('block')
    }
  })
  return el
}

export const createMarkup = (markup) => {
  if (!markup) {
    return
  }
  let el = document.createElement('div')
  el.innerHTML = dompurify.sanitize(markup, {ADD_TAGS: ['iframe']})
  el = addTableClasses(el)
  return { __html: addExternalAria(el) }
}

export const stripTitleHTML = (title) => {
  const div = document.createElement('div')
  div.innerHTML = title.__html
  return div.innerText
}

// True when an RTE value contains actual rendered text. Guards against "empty" rich-text that
// editors leave behind when deleting a heading's text but not its formatting — e.g. `<h2></h2>`,
// `<h2><br></h2>`, or `<h2>&nbsp;</h2>` — which otherwise render an empty heading (an a11y
// violation). Parsing as HTML (rather than a tag-stripping regex) decodes entities like `&nbsp;`
// and collapses whitespace-only markup to nothing. Note: trim() also strips the NBSP (U+00A0)
// that `&nbsp;` decodes to. Safe in the DOM: RTE components render client-side only.
export const hasRenderableText = (value) => {
  if (value == null || typeof value !== 'string') {
    return false
  }
  const el = document.createElement('div')
  el.innerHTML = value
  return (el.textContent || '').trim().length > 0
}

const HEADING_TAG_RE = /<h[1-6]\b/i

/** True when RTE HTML already includes a heading element (h1–h6). */
export const containsHeadingTags = (html) => {
  if (!html || typeof html !== 'string') {
    return false
  }
  return HEADING_TAG_RE.test(html)
}

/**
 * Remove a single outer <p> wrapper so RTE copy can sit inside h2/h3 or a button.
 * The inner group cannot span a </p>, so multi-paragraph copy is left alone rather
 * than being spliced into malformed markup like `One</p><p>Two`.
 */
export const stripSingleParagraphWrapper = (html) =>
  html?.replace(/^\s*<p[^>]*>((?:(?!<\/p>)[\s\S])*)<\/p>\s*$/i, '$1') ?? ''

/**
 * Curated card titles: wrap plain RTE copy in h2/h3 unless the CMS already
 * supplied a heading tag. Returns null when the title should render as-is in a div.
 */
export const resolveCuratedTitleTag = (title, useSmallTitle) => {
  if (!title || containsHeadingTags(title)) {
    return null
  }
  return useSmallTitle ? 'h3' : 'h2'
}

/** RTE `.content` heading scale — applied when curated titles are wrapped in h2/h3. */
export const CURATED_TITLE_HEADING_CLASS = 'content'

export const getCuratedTitleClassName = (titleTag, className = '') => {
  if (!titleTag) {
    return className
  }
  return [CURATED_TITLE_HEADING_CLASS, className].filter(Boolean).join(' ')
}

export const getFriendlyName = (name) => {
  if (!name) {
    return ''
  }
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

export default function createGradientElement({ type, fromColor, fullMobile = false, classes = '' }) {
  if (!type || type == 'None' || !fromColor) {
    return
  }
  return (
    <div
      style={{ '--gradient-from': fromColor }}
      className={`cua--gradient z-0 ${addGradient(type, fullMobile)} ${classes}`}
      aria-hidden="true"
    />
  )
}